import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router, ActivatedRoute } from '@angular/router';
import {
  Subject,
  Subscription,
  combineLatest,
  takeUntil,
  forkJoin,
} from 'rxjs';
import { BaseApiService } from '../services/base-api.service';
import { HelpersService } from '../services/helpers.service';
import { HttpQuery } from '../services/http-query';
import { LoadingService } from '../services/loading.service';
import { CheckSelect } from './check-select';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-base-page',
  template: ``,
})
export class BasePageComponent<T> implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    public helpers: HelpersService,
    public router: Router,
    public activatedRoute: ActivatedRoute,
    public dialog: MatDialog,
    public loading: LoadingService,
    public service: BaseApiService<T>,
    public authService: AuthService
  ) {
    this.selection = new SelectionModel<T>(true, []);
    this.dataSource = new MatTableDataSource<T>([]);
    this.q = {
      page: this.page,
      limit: this.limit,
      search: this.search,
      sortBy: this.sortActive,
      sortDir: this.sortDirection,
      find: this.find,
      status: this.status,
    };
    this.currentUser = this.authService.getLoggedUser();
  }

  unsubs = new Subject();
  titlePrefix!: string;
  pageTitle!: string;
  displayedColumns: any[] = [];
  dataTotal = 0;
  dataSource: MatTableDataSource<T>;
  primaryKey: string = 'id';
  sortActive: string = 'created_at';
  sortDirection = 'desc';
  subject: string = 'name';
  subjectParent?: string | any = null;
  search: string = '';
  searchFields!: string;
  limit = 20;
  page = 1;
  find: any;
  status: any = 'all';
  filters: any = {};
  filterName!: string;
  q: HttpQuery;
  data!: T[];
  selection: SelectionModel<T>;
  select!: CheckSelect<T>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  paginationSub = new Subscription();
  searchSub = new Subscription();
  relation!: string;
  pageSizeOption = [5, 10, 20, 50, 100];
  currentUser: any;

  ngOnInit(): void {
    this.service.onQueryChange.next(this.q);
    setTimeout(() => {
      const getDataLength = this.service.onDataLengthChanged;
      combineLatest([getDataLength])
        .pipe(takeUntil(this.unsubs))
        .subscribe(
          (response) => {
            this.dataTotal = response[0];
          },
          (err) => {
            this.helpers.sbError(err);
          }
        );
      this.afterInit();
    });
  }
  onSearch(val: string) {
    this.search = val;
    if (this.dataSource) {
      this.dataSource.filter = val.trim().toLowerCase();
    }
  }

  applySearch(searchValue: any): void {
    // this.q.search = {
    //   fields: this.searchFields,
    //   value: searchValue
    // }
    this.getData();
  }

  afterInit(): void {
    if (!this.data) this.loading.startLoading();
    this.service.getAll(this.q).then(
      (data) => {
        this.loading.endLoading();
        this.data = data;
        this.dataSource = new MatTableDataSource(data);
        this.select = new CheckSelect(this.dataSource, this.selection);
        this.afterGetData();
      },
      (error) => {
        this.loading.endLoading();
        this.helpers.sbError(error.message);
      }
    );
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.sort.sortChange.subscribe((sort: any) => {
        this.onSortChange(sort);
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubs.next('');
    this.unsubs.complete();
    if (this.paginationSub) {
      this.paginationSub.unsubscribe();
    }
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
    this.select?.selection.clear();
  }

  onPageChange(page: PageEvent): void {
    const { pageSize, pageIndex } = page;
    this.q.limit = pageSize;
    this.q.page = pageIndex + 1;
    this.getData();
    this.select?.selection.clear();
  }

  onSortChange(sort: Sort): void {
    const { active, direction } = sort;
    this.sortActive = active;
    this.sortDirection = direction;
    this.q.sortBy = this.sortActive;
    this.q.sortDir = this.sortDirection;
    this.service.getAll(this.q);
  }

  getData(): void {
    this.loading.startLoading();
    this.service
      .getAll(this.q)
      .then((data) => {
        this.assignData(data);
        this.afterGetData();
        this.loading.endLoading();
      })
      .catch((err) => {
        this.helpers.sbError(err.message);
        this.loading.endLoading();
      });
  }
  assignData(data: T | any): void {
    this.data = data;
    this.dataSource = new MatTableDataSource(data);
    this.select = new CheckSelect(this.dataSource, this.selection);
  }
  afterGetData(): void {}
  add(): void {
    this.router.navigate(['./action'], {
      relativeTo: this.activatedRoute,
      queryParams: {
        m: 'add',
      },
    });
  }
  edit(data: T | any): void {
    this.router.navigate(['./action'], {
      relativeTo: this.activatedRoute,
      queryParams: {
        m: 'edit',
        id: data[this.primaryKey],
      },
      state: {
        data,
      },
    });
  }
  transformSubject(data: any): string {
    return `${
      this.subjectParent
        ? this.helpers.shortIt(data[this.subjectParent][this.subject])
        : this.helpers.shortIt(data[this.subject])
    }`;
  }
  remove(data: any): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '545px',
        maxHeight: '95vh',
        panelClass: 'confirm-dialog',
        data: {
          title: 'Confirm Delete Data',
          message: `This action cannot be undone. Are you sure to delete data ${this.transformSubject(
            data
          )}?`,
        },
      })
      .afterClosed()
      .subscribe((result: any) => {
        if (result) {
          this.loading.startLoading();
          this.service.removeById(data[this.primaryKey]).then(
            () => {
              this.afterRemove();
              this.helpers.sbSuccess(
                `${this.transformSubject(data)} ${this.deleteMessage()}`
              );
              this.loading.endLoading();
            },
            (err) => {
              this.afterRemove();
              this.helpers.sbError(err.message);
            }
          );
        }
      });
  }
  deleteMessage(): string {
    return 'dihapus!';
  }
  afterRemove(): void {
    // custom function after remove event
    this.getData();
    this.select.selection.clear(); // Important!
  }
  deleteSelected(selected: T[]): void {
    if (selected?.length > 0) {
      this.dialog
        .open(ConfirmDialogComponent, {
          panelClass: 'danger-dialog',
          width: '300px',
          maxHeight: '95vh',
          disableClose: true,
          autoFocus: false,
          data: { selected: selected?.length },
        })
        .afterClosed()
        .subscribe((result: any) => {
          if (result) {
            const deleted = selected.map((i: any) => {
              return this.service.removeById(i[this.primaryKey]);
            });
            forkJoin(deleted)
              .pipe(takeUntil(this.unsubs))
              .subscribe(
                () => {
                  this.afterRemove();
                  this.helpers.sbSuccess(
                    `${selected?.length} data ${this.deleteMessage()}`
                  );
                },
                (err) => {
                  this.afterRemove();
                  this.helpers.sbError(err.message);
                }
              );
          }
        });
    }
  }
  parseNo(i: number) {
    return this.q.page && this.q.limit
      ? i + 1 + (this.q.page - 1) * this.q.limit
      : i;
  }
}
